import { Controller } from 'react-hook-form';
import { useParentalApprovalWithForm } from '@/domains/users/hooks/useParentalApprovalWithForm';
import { useFormNavigation, useConfirmationDialog } from '@/shared/hooks';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import SignatureInputBox from '@/components/forms/final-input-boxes/signature-input-box';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { DOCUMENT_LINKS } from '@/constants/config';
import { handleInvalidSubmit } from '@/shared/utils';
import { useEditionDocuments } from '@/shared/hooks/use-edition-documents';

export default function ParentalApprovalPage() {
  const { form, onSubmit, isSubmitting, isDirty } = useParentalApprovalWithForm();
  // SE-14: regulamentul vine din backend (editia curenta), cu fallback pe DOCUMENT_LINKS.
  const documente = useEditionDocuments();

  // Elevul deja activat e mutat de pe pagina asta de AuthenticatedLayout, prin
  // resolvePostAuthTarget (garda punctuala pe /acord-parinti). Efectul care facea aici
  // `navigate('/')` a fost scos pe 2026-08-28: ducea la login si castiga cursa cu layout-ul (2.14).

  // Navigation blocking when form has unsaved changes
  const navigationDialog = useConfirmationDialog({
    onConfirm: () => blocker.proceed?.(),
    onCancel: () => blocker.reset?.(),
  });

  const { blocker } = useFormNavigation({
    isDirty,
    onNavigationAttempt: navigationDialog.show,
  });

  // Submission confirmation
  const submitDialog = useConfirmationDialog({
    onConfirm: async () => {
      const formData = form.getValues();
      await onSubmit(formData);
    },
  });

  return (
    <div className="flex-1 flex flex-col md:overflow-hidden">
      <div className="flex-1 pr-4 ecotic-form-scrollbar md:overflow-y-auto">
        {/* Scrollerul e DOAR de la `md` in sus. Sub `md` formularul curge in
            pagina: un container derulabil pe telefon inseamna ca degetul misca
            altceva decat crede utilizatorul, si ascunde bara de actiuni sub el.
            Masurat la 360px: containerul chiar derula (scrollHeight > clientHeight). */}
        <div className="ecotic-form-card space-y-6">
          {/* `mt-10 md:mt-0`: la 360 titlul (y=39) sta sub hamburgerul flotant (marginea de jos la 68).
              Marja pe h1, nu padding pe `ecotic-form-card` (clasa din main.css). */}
          <h1 className="mt-10 text-2xl font-bold text-foreground md:mt-0">
            Formular de colectare al acordului parental
          </h1>
          <p className="text-sm text-gray-600 m-0">
            Contul tau de elev se activeaza dupa ce un parinte sau tutore completeaza si
            semneaza acordul de mai jos. Dureaza cateva minute; dupa trimitere vei putea
            intra in platforma.
          </p>
          <Card className="w-full max-w-4xl">
            <CardHeader>
              <CardTitle>Acord Parental</CardTitle>
            </CardHeader>
        <Form {...form}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const isValid = await form.trigger();
              if (isValid) {
                submitDialog.show();
              } else {
                handleInvalidSubmit();
              }
            }}
          >
            <CardContent className="space-y-4">
              {/* Parent Approval */}
              <FormField
                control={form.control}
                name="parentApproval"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        In calitate de tutore al elevului, imi dau aprobarea pentru participarea acestuia in cadrul programului educational Scoala Ecoterrienilor.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {/* Confirmam Inscrierea Minorului */}
              <FormField
                control={form.control}
                name="prinPrezentaConfirmamInscriereaMinorului"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Prin prezenta confirmam inscrierea minorului in Campania „Scoala Ecoterrienilor" si acceptarea deplina si neconditionata a Regulamentului disponibil pe www.ecotic.ro.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {/* Ecotic Poate Folosi Imagini */}
              <FormField
                control={form.control}
                name="ecoticPoateFolosiImagini"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        ECOTIC poate folosi imagini in care pot aparea participantii in urmatoarele scopuri: ca dovada ca s-au realizat activitatile propuse in Concurs, pentru promovarea rezultatelor Concursului pe site-ul ECOTIC si/sau paginile de social media ale ECOTIC, pentru a fi incluse in materiale de prezentare a activitatii ECOTIC (tip retrospectiva, newsletter etc.). Cu toate acestea, inteleg ca Organizatorii recomanda transmiterea de imagini fara identificarea faciala a persoanelor.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {/* Am Fost Informati */}
              <FormField
                control={form.control}
                name="amFostInformati"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Am fost informati despre modul de desfasurare al campaniei, procedura de colectare si regulamentul campaniei si suntem de acord cu toate acestea. Confirmam ca ni s-a adus la cunostinta si acceptam posibilitatea modificarii, suspendarii sau incetarii Campaniei care ni se aplica, caz in care aceste sunt postate pe www.ecotic.ro.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {/* Confirm Ca Am Citit Informarea */}
              <FormField
                control={form.control}
                name="prinPrezentaConfirmCaAmCititInformarea"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Prin prezenta confirm ca am citit Informarea (Nota de informare Scoala Ecoterrienilor - https://www.ecotic.ro/informare-scoala-ecoterrienilor/ si Nota de informare inscriere elevi in Scoala Ecoterrienilor - https://www.ecotic.ro/nota-de-informare-inscriere-elevi-in-scoala-ecoterrienilor-2/) cu privire la prelucrarea datelor mele cu caracter personal si inteleg ca prelucrarea datelor mele personale (email) este strict necesara pentru inscrierea si participarea la competitie, conform Regulamentului, care este acordul partilor (eu, pe de o parte, ECOTIC si ECOTIC BAT, pe de alta parte). Inteleg ca, in cazul in care nu doresc ca ECOTIC sa imi prelucreze datele cu caracter personal, pot sa nu bifez acest camp, iar acest formular nu va transmite datele mele catre ASOCIATIA ECOTIC si ECOTIC BAT si nu voi putea fi inscris in competitia Scoala Ecoterrienilor, relatia contractuala dintre parti neputandu-se incheia.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {/* Confirm Ca In Cazul In Care Proiectul */}
              <FormField
                control={form.control}
                name="confirmCaInCazulInCareProiectul"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Confirm ca in cazul in care proiectul inscris include imagini cu persoane (majore sau minore), acestea si-au dat consimtamantul expres, direct sau prin reprezentantul legal, pentru utilizarea imaginilor de catre Organizator.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {/* Consimt Pentru Pastrarea Datelor Personale */}
              <FormField
                control={form.control}
                name="consimtPentruPastrareaDatelorPersonale"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Consimt pentru pastrarea datelor personale ale minorului in baza de date a ASOCIATIEI ECOTIC pentru o perioada de maxim 5 ani cu scopul de a primi materiale informative pe tema protectiei mediului si invitatie de inscriere la editia urmatoare a proiectului sau la alte proiecte pe tema protectiei mediului.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {/* Am Citit Si Sunt De Acord */}
              <FormField
                control={form.control}
                name="amCititSiSuntDeAcord"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      {/* `block`, DOAR aici: `Label` e `flex items-center gap-2`, iar un text urmat
                          de doua <a> devine trei flex-items nowrap — la 360 un cuvant pe rand, cu
                          eticheta iesita 46px din rand si «Regulamentul Campaniei» 21px peste card
                          (masurat pe 63e5aa09). Celelalte sapte etichete au doar text, deci un
                          singur flex-item, si nu au problema. `Label`-ul comun ramane neatins. */}
                      <FormLabel className="block">
                        Am citit si sunt de acord cu:
                        {/* Linkurile stau pe randuri proprii, fiecare cu 44px sub `md` (directia S1,
                            varianta a). Inline in propozitie, cu `leading-none`, randurile ajungeau
                            la 14px unul de altul: zonele extinse prin `::after` se suprapuneau, iar
                            «Nota de Informare» nu mai putea fi atinsa deloc (masurat dupa `block`). */}
                        <a
                          href={DOCUMENT_LINKS.NOTA_INFORMARE_ELEVI}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex w-fit min-h-11 items-center text-magenta underline hover:text-magenta-hover md:min-h-0 md:py-1"
                        >
                          Nota de Informare
                        </a>
                        <a
                          href={documente.regulament}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex w-fit min-h-11 items-center text-magenta underline hover:text-magenta-hover md:min-h-0 md:py-1"
                        >
                          Regulamentul Campaniei
                        </a>
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {/* Signature Field */}
              <Controller
                control={form.control}
                name="signature"
                render={({ field, fieldState }) => (
                  <SignatureInputBox
                    // SE-27: butonul nu se mai numeste «Încarcă», deci nici instructiunea.
                    label="Semnatura parintelui (dupa completare, apasati „Salvare” sub caseta)"
                    handleChange={(e) => field.onChange(e.target.value)}
                    name={field.name}
                    value={field.value}
                    errors={fieldState.error ? { [field.name]: fieldState.error.message } : {}}
                  />
                )}
              />
            </CardContent>
            <CardFooter className="justify-end">
              {/* 🔴 SE-27, efect lateral masurat: dupa ce butonul semnaturii a devenit «Salvare»,
                  ecranul avea DOUA butoane numite identic — cel al semnaturii si acesta, care
                  trimite tot acordul. Playwright a picat pe loc, cu „strict mode violation",
                  adica ambiguitatea e reala, nu estetica.
                  Butonul semnaturii pastreaza cuvantul cerut de client; acesta isi ia numele
                  actiunii lui, care oricum era mai exact: trimite acordul, nu salveaza o ciorna. */}
              <Button size="lg" type="submit" disabled={!isDirty || isSubmitting}>
                {isSubmitting ? 'Se trimite...' : 'Trimite acordul'}
              </Button>
            </CardFooter>
          </form>
        </Form>
          </Card>
        </div> {/* ecotic-form-card */}
      </div> {/* ecotic-form-scrollbar */}

      {/* Navigation confirmation */}
      <ConfirmationDialog
        isOpen={navigationDialog.isOpen}
        onClose={navigationDialog.handleCancel}
        onConfirm={navigationDialog.handleConfirm}
        title="Modificări nesalvate"
        description="Aveți modificări nesalvate. Doriți să părăsiți pagina?"
        confirmText="Părăsește pagina"
        cancelText="Rămâi pe pagină"
        variant="destructive"
      />

      {/* Submission confirmation */}
      <ConfirmationDialog
        isOpen={submitDialog.isOpen}
        onClose={submitDialog.handleCancel}
        onConfirm={submitDialog.handleConfirm}
        title="Confirmare salvare"
        description="Doriți să salvați formularul?"
        confirmText="Salvează"
        cancelText="Anulare"
        loading={isSubmitting}
      />
    </div>
  );
}
